/**
 * IndexedDB storage layer for multi-character management.
 * Uses the `idb` library for a lightweight, promise-based IndexedDB wrapper.
 */
import { openDB, type IDBPDatabase } from 'idb';
import type { CharacterDraft } from '../stores/types/characterDraft';
import type { RuntimeState } from './characterExport';

// ============================================================================
// Constants
// ============================================================================

const DB_NAME = 'character-creator';
const DB_VERSION = 1;
const STORE_NAME = 'characters';

// ============================================================================
// Types
// ============================================================================

/**
 * Lightweight summary for character list display.
 * Denormalized to avoid loading full draft for list views.
 */
export interface CharacterSummary {
  raceId: string | null;
  classId: string | null;
  level: number;
}

/**
 * Complete stored character record in IndexedDB.
 */
export interface StoredCharacter {
  /** UUID - primary key */
  id: string;
  /** Character name */
  name: string;
  /** Ruleset ID (e.g., 'srd-5.1') */
  rulesetId: string;
  /** Full character draft data */
  draft: CharacterDraft;
  /** Optional runtime state (HP, spell slots, etc.) */
  runtimeState?: RuntimeState;
  /** Denormalized summary for list performance */
  summary: CharacterSummary;
  /** ISO 8601 timestamp when created */
  createdAt: string;
  /** ISO 8601 timestamp when last updated */
  updatedAt: string;
}

/**
 * Standardized result object for all database operations.
 */
export interface DatabaseResult<T> {
  success: boolean;
  data?: T;
  warnings: string[];
  errors: string[];
}

/**
 * Storage quota information.
 */
export interface StorageQuota {
  used: number;
  quota: number;
  percentUsed: number;
}

// ============================================================================
// Database Singleton
// ============================================================================

let dbInstance: IDBPDatabase | null = null;

/**
 * Get or initialize the database connection.
 */
async function getDb(): Promise<IDBPDatabase> {
  if (dbInstance) {
    return dbInstance;
  }

  dbInstance = await openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Create the characters object store
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        // Create indexes for efficient querying
        store.createIndex('updatedAt', 'updatedAt');
        store.createIndex('rulesetId', 'rulesetId');
        store.createIndex('name', 'name');
        store.createIndex('createdAt', 'createdAt');
      }
    },
  });

  return dbInstance;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if IndexedDB is supported in the current browser.
 */
export function isIndexedDBSupported(): boolean {
  return typeof indexedDB !== 'undefined';
}

/**
 * Generate a character summary from a draft for list display.
 */
export function generateCharacterSummary(draft: CharacterDraft): CharacterSummary {
  // Get primary class ID (first class in classLevels array)
  const primaryClassId =
    draft.classLevels && draft.classLevels.length > 0
      ? draft.classLevels[0].classId
      : draft.classId;

  return {
    raceId: draft.raceId,
    classId: primaryClassId,
    level: draft.characterLevel || 1,
  };
}

/**
 * Type guard to validate a stored character object.
 */
export function validateStoredCharacter(
  data: unknown
): data is StoredCharacter {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;

  // Required string fields
  if (typeof obj.id !== 'string' || !obj.id) return false;
  if (typeof obj.name !== 'string') return false;
  if (typeof obj.rulesetId !== 'string' || !obj.rulesetId) return false;
  if (typeof obj.createdAt !== 'string') return false;
  if (typeof obj.updatedAt !== 'string') return false;

  // Required object fields
  if (!obj.draft || typeof obj.draft !== 'object') return false;
  if (!obj.summary || typeof obj.summary !== 'object') return false;

  return true;
}

/**
 * Create a success result.
 */
function successResult<T>(data: T, warnings: string[] = []): DatabaseResult<T> {
  return { success: true, data, warnings, errors: [] };
}

/**
 * Create an error result.
 */
function errorResult<T>(errors: string[]): DatabaseResult<T> {
  return { success: false, warnings: [], errors };
}

// ============================================================================
// Core CRUD Operations
// ============================================================================

/**
 * Initialize the database. Call this early in app startup.
 */
export async function initDatabase(): Promise<DatabaseResult<void>> {
  if (!isIndexedDBSupported()) {
    return errorResult(["Your browser doesn't support offline storage."]);
  }

  try {
    await getDb();
    return successResult(undefined);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Failed to initialize database';
    return errorResult([message]);
  }
}

/**
 * Save a character to the database (create or update).
 */
export async function saveCharacter(
  draft: CharacterDraft,
  runtimeState?: RuntimeState
): Promise<DatabaseResult<StoredCharacter>> {
  if (!isIndexedDBSupported()) {
    return errorResult(["Your browser doesn't support offline storage."]);
  }

  try {
    const db = await getDb();
    const now = new Date().toISOString();

    // Generate ID if not present
    const id = draft.id || crypto.randomUUID();

    // Check if character already exists
    const existing = await db.get(STORE_NAME, id);

    const character: StoredCharacter = {
      id,
      name: draft.name || 'Unnamed Character',
      rulesetId: draft.rulesetId,
      draft: { ...draft, id, updatedAt: now },
      summary: generateCharacterSummary(draft),
      createdAt: existing?.createdAt || draft.createdAt || now,
      updatedAt: now,
    };

    // Include runtime state if provided
    if (runtimeState) {
      character.runtimeState = runtimeState;
    }

    await db.put(STORE_NAME, character);

    return successResult(character);
  } catch (error) {
    // Check for quota exceeded
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      return errorResult([
        'Storage full. Please delete some characters to free up space.',
      ]);
    }

    const message =
      error instanceof Error ? error.message : 'Failed to save character';
    return errorResult([message]);
  }
}

/**
 * Get a character by ID.
 */
export async function getCharacter(
  id: string
): Promise<DatabaseResult<StoredCharacter>> {
  if (!isIndexedDBSupported()) {
    return errorResult(["Your browser doesn't support offline storage."]);
  }

  try {
    const db = await getDb();
    const character = await db.get(STORE_NAME, id);

    if (!character) {
      return errorResult(['Character not found.']);
    }

    if (!validateStoredCharacter(character)) {
      return errorResult(['Invalid character data in storage.']);
    }

    return successResult(character);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Failed to load character';
    return errorResult([message]);
  }
}

/**
 * Get all characters, sorted by updatedAt (newest first).
 */
export async function getAllCharacters(): Promise<
  DatabaseResult<StoredCharacter[]>
> {
  if (!isIndexedDBSupported()) {
    return errorResult(["Your browser doesn't support offline storage."]);
  }

  try {
    const db = await getDb();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const index = tx.store.index('updatedAt');

    // Get all characters and sort by updatedAt descending
    const characters: StoredCharacter[] = [];
    let cursor = await index.openCursor(null, 'prev'); // 'prev' for descending

    while (cursor) {
      if (validateStoredCharacter(cursor.value)) {
        characters.push(cursor.value);
      }
      cursor = await cursor.continue();
    }

    return successResult(characters);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Failed to load characters';
    return errorResult([message]);
  }
}

/**
 * Get lightweight summaries of all characters for list display.
 * More efficient than loading full drafts.
 */
export async function getAllCharacterSummaries(): Promise<
  DatabaseResult<
    Array<{
      id: string;
      name: string;
      rulesetId: string;
      summary: CharacterSummary;
      createdAt: string;
      updatedAt: string;
    }>
  >
> {
  if (!isIndexedDBSupported()) {
    return errorResult(["Your browser doesn't support offline storage."]);
  }

  try {
    const db = await getDb();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const index = tx.store.index('updatedAt');

    const summaries: Array<{
      id: string;
      name: string;
      rulesetId: string;
      summary: CharacterSummary;
      createdAt: string;
      updatedAt: string;
    }> = [];

    let cursor = await index.openCursor(null, 'prev');

    while (cursor) {
      const record = cursor.value;
      if (validateStoredCharacter(record)) {
        summaries.push({
          id: record.id,
          name: record.name,
          rulesetId: record.rulesetId,
          summary: record.summary,
          createdAt: record.createdAt,
          updatedAt: record.updatedAt,
        });
      }
      cursor = await cursor.continue();
    }

    return successResult(summaries);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Failed to load character list';
    return errorResult([message]);
  }
}

/**
 * Delete a character by ID.
 */
export async function deleteCharacter(
  id: string
): Promise<DatabaseResult<void>> {
  if (!isIndexedDBSupported()) {
    return errorResult(["Your browser doesn't support offline storage."]);
  }

  try {
    const db = await getDb();

    // Check if character exists
    const existing = await db.get(STORE_NAME, id);
    if (!existing) {
      return errorResult(['Character not found.']);
    }

    await db.delete(STORE_NAME, id);
    return successResult(undefined);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Failed to delete character';
    return errorResult([message]);
  }
}

/**
 * Check if a character exists by ID.
 */
export async function characterExists(
  id: string
): Promise<DatabaseResult<boolean>> {
  if (!isIndexedDBSupported()) {
    return errorResult(["Your browser doesn't support offline storage."]);
  }

  try {
    const db = await getDb();
    const character = await db.get(STORE_NAME, id);
    return successResult(!!character);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Failed to check character';
    return errorResult([message]);
  }
}

// ============================================================================
// Additional Operations
// ============================================================================

/**
 * Duplicate a character with a new ID.
 */
export async function duplicateCharacter(
  id: string
): Promise<DatabaseResult<StoredCharacter>> {
  if (!isIndexedDBSupported()) {
    return errorResult(["Your browser doesn't support offline storage."]);
  }

  try {
    // Get the original character
    const result = await getCharacter(id);
    if (!result.success || !result.data) {
      return errorResult(result.errors);
    }

    const original = result.data;
    const now = new Date().toISOString();
    const newId = crypto.randomUUID();

    // Create the duplicate with new ID and timestamps
    const duplicate: StoredCharacter = {
      ...original,
      id: newId,
      name: `${original.name} (Copy)`,
      draft: {
        ...original.draft,
        id: newId,
        createdAt: now,
        updatedAt: now,
      },
      createdAt: now,
      updatedAt: now,
    };

    const db = await getDb();
    await db.put(STORE_NAME, duplicate);

    return successResult(duplicate);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Failed to duplicate character';
    return errorResult([message]);
  }
}

/**
 * Rename a character.
 */
export async function renameCharacter(
  id: string,
  newName: string
): Promise<DatabaseResult<StoredCharacter>> {
  if (!isIndexedDBSupported()) {
    return errorResult(["Your browser doesn't support offline storage."]);
  }

  const trimmedName = newName.trim();
  if (!trimmedName) {
    return errorResult(['Character name cannot be empty.']);
  }

  try {
    const db = await getDb();
    const character = await db.get(STORE_NAME, id);

    if (!character || !validateStoredCharacter(character)) {
      return errorResult(['Character not found.']);
    }

    const now = new Date().toISOString();
    const updated: StoredCharacter = {
      ...character,
      name: trimmedName,
      draft: {
        ...character.draft,
        name: trimmedName,
        updatedAt: now,
      },
      updatedAt: now,
    };

    await db.put(STORE_NAME, updated);
    return successResult(updated);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Failed to rename character';
    return errorResult([message]);
  }
}

/**
 * Get storage quota information.
 */
export async function getStorageQuota(): Promise<DatabaseResult<StorageQuota>> {
  if (!isIndexedDBSupported()) {
    return errorResult(["Your browser doesn't support offline storage."]);
  }

  try {
    // Use Storage API if available
    if (navigator.storage && navigator.storage.estimate) {
      const estimate = await navigator.storage.estimate();
      const used = estimate.usage || 0;
      const quota = estimate.quota || 0;
      const percentUsed = quota > 0 ? (used / quota) * 100 : 0;

      const warnings: string[] = [];
      if (percentUsed > 80) {
        warnings.push(
          'Storage is getting full. Consider deleting old characters.'
        );
      }

      return successResult({ used, quota, percentUsed }, warnings);
    }

    // Fallback: estimate based on character count
    const result = await getAllCharacters();
    if (!result.success) {
      return errorResult(result.errors);
    }

    // Rough estimate: ~50KB per character
    const estimatedUsed = (result.data?.length || 0) * 50 * 1024;

    return successResult({
      used: estimatedUsed,
      quota: 0, // Unknown
      percentUsed: 0,
    });
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Failed to get storage quota';
    return errorResult([message]);
  }
}

/**
 * Clear all characters from the database.
 * USE WITH CAUTION - primarily for testing and development.
 */
export async function clearAllCharacters(): Promise<DatabaseResult<void>> {
  if (!isIndexedDBSupported()) {
    return errorResult(["Your browser doesn't support offline storage."]);
  }

  try {
    const db = await getDb();
    await db.clear(STORE_NAME);
    return successResult(undefined);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Failed to clear characters';
    return errorResult([message]);
  }
}

/**
 * Get characters filtered by ruleset ID.
 */
export async function getCharactersByRuleset(
  rulesetId: string
): Promise<DatabaseResult<StoredCharacter[]>> {
  if (!isIndexedDBSupported()) {
    return errorResult(["Your browser doesn't support offline storage."]);
  }

  try {
    const db = await getDb();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const index = tx.store.index('rulesetId');

    const characters: StoredCharacter[] = [];
    let cursor = await index.openCursor(IDBKeyRange.only(rulesetId));

    while (cursor) {
      if (validateStoredCharacter(cursor.value)) {
        characters.push(cursor.value);
      }
      cursor = await cursor.continue();
    }

    // Sort by updatedAt descending
    characters.sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );

    return successResult(characters);
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : 'Failed to load characters by ruleset';
    return errorResult([message]);
  }
}

/**
 * Close the database connection (primarily for testing).
 */
export function closeDatabase(): void {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
}
